import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { AlertCircle, ExternalLink, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ToolDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTool();
  }, [id]);

  const fetchTool = async () => {
    try {
      const response = await axios.get(`${API}/tools/${id}`);
      setTool(response.data);
    } catch (error) {
      console.error('Error fetching tool:', error);
      toast.error('Failed to load tool details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="py-20 text-center" data-testid="loading-state">
          <p className="text-xl text-[#333333]">Loading tool details...</p>
        </div>
      </Layout>
    );
  }

  if (!tool) {
    return (
      <Layout>
        <div className="py-20 text-center" data-testid="not-found">
          <p className="text-xl text-[#333333]">Tool not found</p>
          <Button
            onClick={() => navigate('/')}
            className="mt-6 bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-12 px-8 rounded-full font-semibold text-lg"
          >
            Go Home
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="text-base text-[#0044CC] hover:underline focus-visible-ring rounded"
              data-testid="back-button"
            >
              ← Back to results
            </button>
          </div>

          {tool.data_staleness_flag && (
            <div className="mb-8 p-6 bg-yellow-50 border-2 border-yellow-200 rounded-2xl flex items-start gap-4" data-testid="stale-data-notice">
              <AlertCircle className="h-6 w-6 text-black flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-black mb-2">Details may be out of date</h3>
                <p className="text-base text-[#333333]">
                  This tool's information was last updated {Math.floor((Date.now() - new Date(tool.last_updated)) / (1000 * 60 * 60 * 24))} days ago.
                  Please verify current details on the official website.
                </p>
              </div>
            </div>
          )}

          <div className="bg-white border-2 border-gray-100 p-8 md:p-12 rounded-2xl">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <h1 className="text-4xl md:text-5xl font-semibold text-black" data-testid="tool-name">
                {tool.name}
              </h1>
              <span className="px-4 py-2 bg-gray-100 text-black text-base font-medium rounded-full">
                {tool.primary_category}
              </span>
            </div>

            <p className="text-xl md:text-2xl leading-relaxed text-[#333333] mb-8" data-testid="tool-description">
              {tool.description_plain}
            </p>

            <Button
              onClick={() => window.open(tool.official_website, '_blank')}
              className="bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-12 px-8 rounded-full font-semibold text-lg transition-colors inline-flex items-center gap-2 mb-12"
              data-testid="visit-official-website-button"
            >
              Visit Official Website
              <ExternalLink className="h-5 w-5" />
            </Button>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-black mb-4">Pricing</h2>
                <div className="space-y-2">
                  <p className="text-base text-[#333333]">
                    <strong className="text-black">Model:</strong> {tool.pricing_model}
                  </p>
                  {tool.starting_price && (
                    <p className="text-base text-[#333333]">
                      <strong className="text-black">Starting Price:</strong> {tool.starting_price}
                    </p>
                  )}
                  {tool.free_trial_duration && (
                    <p className="text-base text-[#333333]">
                      <strong className="text-black">Free Trial:</strong> {tool.free_trial_duration}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-black mb-4">Details</h2>
                <div className="space-y-2">
                  <p className="text-base text-[#333333]">
                    <strong className="text-black">Skill Level:</strong> {tool.skill_level}
                  </p>
                  <p className="text-base text-[#333333]">
                    <strong className="text-black">Enterprise Ready:</strong> {tool.enterprise_ready}
                  </p>
                  <p className="text-base text-[#333333]">
                    <strong className="text-black">API Available:</strong> {tool.api_available}
                  </p>
                  <p className="text-base text-[#333333]">
                    <strong className="text-black">Agentic Capability:</strong> {tool.agentic_capability}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-semibold text-black mb-4">Supported Platforms</h2>
              <div className="flex flex-wrap gap-3">
                {tool.supported_platforms.map((platform, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-gray-100 text-black text-base font-medium rounded-full"
                  >
                    {platform}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-semibold text-black mb-4">Best For</h2>
              <ul className="space-y-3">
                {tool.best_for.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-[#037F4C] flex-shrink-0 mt-0.5" />
                    <span className="text-base md:text-lg text-[#333333]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-black mb-4">Pros</h2>
                <ul className="space-y-3">
                  {tool.pros.map((pro, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-[#037F4C] flex-shrink-0 mt-0.5" />
                      <span className="text-base text-[#333333]">{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-black mb-4">Limitations</h2>
                <ul className="space-y-3">
                  {tool.limitations.map((limitation, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <X className="h-5 w-5 text-[#D92D20] flex-shrink-0 mt-0.5" />
                      <span className="text-base text-[#333333]">{limitation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-semibold text-black mb-4">Privacy</h2>
              <div className="space-y-3">
                <p className="text-base text-[#333333]">
                  <strong className="text-black">Privacy Level:</strong> {tool.privacy_level}
                </p>
                <p className="text-base md:text-lg leading-relaxed text-[#333333]">
                  {tool.privacy_notes}
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-semibold text-black mb-4">Tags</h2>
              <div className="flex flex-wrap gap-2">
                {tool.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 text-black text-sm rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ToolDetailPage;